import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useGetAgentsById, useGetAgentLogs } from '@/api/control/control-plane/control-plane'
import { AgentHealthStatus } from '@/components/agents/AgentHealthStatus'
import { ConfirmAgentHealthButton } from '@/components/agents/ConfirmAgentHealthButton'
import { getHealthCheckRefreshInterval } from '@/lib/agentHealth'
import { useRoles } from '@/contexts/RolesContext'
import type { AgentLogEntryDto } from '@/api/control/missionImpactAPI.schemas'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { QueryErrorState } from '@/components/common/QueryErrorState'
import { PageHeader } from '@/components/common/PageHeader'
import { ChevronLeft, ChevronDown, ChevronUp } from 'lucide-react'
import { useLanguage } from '@/contexts/LanguageContext'
import type { AgentDtoStatus } from '@/api/control/missionImpactAPI.schemas'

/**
 * Returns the badge variant for an agent status string.
 */
function getStatusBadgeVariant(status: AgentDtoStatus): 'default' | 'secondary' | 'destructive' {
  switch (status) {
    case 'Active':
      return 'default'
    case 'Unregistered':
      return 'secondary'
    case 'Inactive':
      return 'destructive'
    default:
      return 'secondary'
  }
}

/**
 * Returns a badge variant for a log level string.
 */
function getLevelBadgeVariant(level: string | undefined): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (level?.toLowerCase() ?? '') {
    case 'error':
      return 'destructive'
    case 'heartbeat':
      return 'outline'
    default:
      return 'secondary'
  }
}

/**
 * Agent detail page showing agent information and paginated log entries.
 */
export default function AgentDetailPage() {
  const { orgId, agentId } = useParams<{ orgId: string; agentId: string }>()
  const navigate = useNavigate()
  const { t } = useLanguage()
  const { isAdminOrOperator } = useRoles()

  const [continuationToken, setContinuationToken] = useState<string | undefined>(undefined)
  const [allLogs, setAllLogs] = useState<AgentLogEntryDto[]>([])
  const [nextToken, setNextToken] = useState<string | null | undefined>(undefined)
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set())

  const {
    data: agentResponse,
    isLoading: agentLoading,
    error: agentError,
    refetch: refetchAgent,
  } = useGetAgentsById(orgId ?? '', agentId ?? '', {
    query: {
      refetchInterval: (query) => {
        const current = query.state.data?.data
        return getHealthCheckRefreshInterval(current ? [current] : undefined, Date.now())
      },
    },
  })

  const {
    data: logsData,
    isLoading: logsLoading,
    error: logsError,
    refetch: refetchLogs,
  } = useGetAgentLogs(orgId ?? '', agentId ?? '', { limit: 50, continuationToken })

  useEffect(() => {
    if (logsData?.data) {
      const items = logsData.data.items ?? []
      setAllLogs(prev => (continuationToken ? [...prev, ...items] : items))
      setNextToken(logsData.data.continuationToken)
    }
  }, [logsData, continuationToken])

  const agent = agentResponse?.data

  if (!orgId || !agentId) {
    return null
  }

  return (
    <div className="space-y-6">
      <Button
        variant="ghost"
        size="sm"
        className="self-start -mb-2"
        onClick={() => { void navigate(`/dashboard/orgs/${orgId}/agents`); }}
      >
        <ChevronLeft className="mr-1 h-4 w-4" />
        {t.agents.detailPage.backButton}
      </Button>
      <PageHeader title={agent?.name ?? t.agents.detailPage.title} description="" />

      {/* Agent info card */}
      <Card>
        <CardHeader>
          <CardTitle>{t.agents.detailPage.infoCard.title}</CardTitle>
          {agentLoading && <CardDescription>{t.agents.loadingAgents}</CardDescription>}
        </CardHeader>
        <CardContent>
          {agentError ? (
            <QueryErrorState
              error={agentError}
              message={t.agents.errors.failedToLoadAgents}
              onRetry={() => { void refetchAgent(); }}
            />
          ) : agentLoading ? (
            <dl className="grid grid-cols-2 gap-4 sm:grid-cols-3">
              {[...Array<undefined>(5)].map((_, i) => (
                <div key={i}>
                  <Skeleton className="h-3 w-24 mb-2" />
                  <Skeleton className="h-5 w-32" />
                </div>
              ))}
            </dl>
          ) : agent ? (
            <dl className="grid grid-cols-2 gap-4 sm:grid-cols-3">
              <div>
                <dt className="text-sm text-muted-foreground">{t.agents.detailPage.infoCard.status}</dt>
                <dd className="mt-1">
                  <Badge variant={getStatusBadgeVariant(agent.status)}>
                    {t.agents.status[agent.status]}
                  </Badge>
                </dd>
              </div>
              <div>
                <dt className="text-sm text-muted-foreground">{t.agents.health.label}</dt>
                <dd className="mt-1 flex items-start gap-2">
                  <AgentHealthStatus agent={agent} />
                  {isAdminOrOperator && <ConfirmAgentHealthButton orgId={orgId} agent={agent} />}
                </dd>
              </div>
              <div>
                <dt className="text-sm text-muted-foreground">{t.agents.healthCheck.lastResult}</dt>
                <dd className="mt-1 text-sm">
                  {agent.lastHealthCheckCompletedUtc ? (
                    <>
                      {new Date(agent.lastHealthCheckCompletedUtc).toLocaleString()}
                      {agent.lastHealthCheckDetail && (
                        <span className="block text-xs text-muted-foreground">{agent.lastHealthCheckDetail}</span>
                      )}
                    </>
                  ) : (
                    '-'
                  )}
                </dd>
              </div>
              <div>
                <dt className="text-sm text-muted-foreground">{t.agents.detailPage.infoCard.version}</dt>
                <dd className="mt-1 text-sm">{agent.version ?? '-'}</dd>
              </div>
              <div>
                <dt className="text-sm text-muted-foreground">{t.agents.editDialog.workerCountLabel}</dt>
                <dd className="mt-1 text-sm">{agent.workerCount}</dd>
              </div>
              <div>
                <dt className="text-sm text-muted-foreground">{t.agents.detailPage.infoCard.lastHeartbeat}</dt>
                <dd className="mt-1 text-sm">
                  {agent.lastHeartbeatUtc ? new Date(agent.lastHeartbeatUtc).toLocaleString() : '-'}
                </dd>
              </div>
              <div>
                <dt className="text-sm text-muted-foreground">{t.agents.detailPage.infoCard.registeredAt}</dt>
                <dd className="mt-1 text-sm">
                  {agent.registeredAtUtc ? new Date(agent.registeredAtUtc).toLocaleString() : '-'}
                </dd>
              </div>
              <div>
                <dt className="text-sm text-muted-foreground">{t.agents.detailPage.infoCard.lastError}</dt>
                <dd className="mt-1 text-sm">
                  {agent.lastErrorUtc ? (
                    <span className="text-destructive">{new Date(agent.lastErrorUtc).toLocaleString()}</span>
                  ) : (
                    t.agents.detailPage.infoCard.noError
                  )}
                </dd>
              </div>
            </dl>
          ) : null}
        </CardContent>
      </Card>

      {/* Logs card */}
      <Card>
        <CardHeader>
          <CardTitle>{t.agents.detailPage.logs.title}</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {logsError ? (
            <div className="p-6">
              <QueryErrorState
                error={logsError}
                message={t.agents.detailPage.logs.error}
                onRetry={() => { void refetchLogs(); }}
              />
            </div>
          ) : logsLoading && allLogs.length === 0 ? (
            <>
              <Table className="[&_tr>*:first-child]:pl-8 [&_tr>*:last-child]:pr-8">
                <TableHeader>
                  <TableRow className="border-t">
                    <TableHead className="w-28">{t.agents.detailPage.logs.level}</TableHead>
                    <TableHead className="w-32">{t.agents.detailPage.logs.source}</TableHead>
                    <TableHead>{t.agents.detailPage.logs.message}</TableHead>
                    <TableHead className="w-44 text-right">{t.agents.detailPage.logs.timestamp}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {[...Array<undefined>(5)].map((_, i) => (
                    <TableRow key={i}>
                      <TableCell><Skeleton className="h-5 w-16" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-full" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-32 ml-auto" /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <div className="flex items-center justify-between px-6 py-4 border-t">
                <Skeleton className="h-4 w-40" />
              </div>
            </>
          ) : allLogs.length === 0 ? (
            <div className="text-center text-muted-foreground py-8">
              <p>{t.agents.detailPage.logs.noLogs}</p>
            </div>
          ) : (
            <>
              <Table className="[&_tr>*:first-child]:pl-8 [&_tr>*:last-child]:pr-8">
                <TableHeader>
                  <TableRow className="border-t">
                    <TableHead className="w-28">{t.agents.detailPage.logs.level}</TableHead>
                    <TableHead className="w-32">{t.agents.detailPage.logs.source}</TableHead>
                    <TableHead>{t.agents.detailPage.logs.message}</TableHead>
                    <TableHead className="w-44 text-right">{t.agents.detailPage.logs.timestamp}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {allLogs.map((entry, idx) => {
                    const message = entry.message ?? ''
                    const isLong = message.length > 1000
                    const isExpanded = expandedRows.has(idx)
                    const displayMessage = isLong && !isExpanded
                      ? message.slice(0, 1000) + '…'
                      : message
                    return (
                      <TableRow key={idx}>
                        <TableCell>
                          <Badge variant={getLevelBadgeVariant(entry.level)}>{entry.level}</Badge>
                        </TableCell>
                        <TableCell className="text-muted-foreground">{entry.source}</TableCell>
                        <TableCell className="font-mono text-xs whitespace-normal max-w-md">
                          <span>{displayMessage}</span>
                          {isLong && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-5 w-5 p-0 ml-1 inline-flex align-middle"
                              onClick={() => {
                                setExpandedRows(prev => {
                                  const next = new Set(prev)
                                  if (isExpanded) next.delete(idx); else next.add(idx)
                                  return next
                                })
                              }}
                            >
                              {isExpanded
                                ? <ChevronUp className="h-3 w-3" />
                                : <ChevronDown className="h-3 w-3" />}
                            </Button>
                          )}
                        </TableCell>
                        <TableCell className="text-right text-xs">
                          <span className="text-muted-foreground">{entry.serverReceivedAtUtc ? new Date(entry.serverReceivedAtUtc).toLocaleString() : ''}</span>
                          <br />
                          <span className="text-muted-foreground/60">{t.agents.detailPage.logs.agentTime} {entry.timestampUtc ? new Date(entry.timestampUtc).toLocaleString() : ''}</span>
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
              <div className="flex items-center justify-between px-6 py-4 border-t">
                <p className="text-sm text-muted-foreground">
                  {t.agents.detailPage.logs.showing.replace('{count}', String(allLogs.length))}
                </p>
                {nextToken && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => { setContinuationToken(nextToken); }}
                    disabled={logsLoading}
                  >
                    {t.agents.detailPage.logs.loadMore}
                  </Button>
                )}
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
